import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PermissionGate from "../../components/common/PermissionGate";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import { PERMISSIONS } from "../../constants/permissions";
import UserTimeLogsTab from "../../components/schedule/UserTimeLogsTab";
import { usePermission } from "../../hooks/usePermission";
import "./UserManagementPage.css";
import api, { subscriptionAPI, usersAPI } from "../../api/client";
import { formatLocalDate, todayString } from "../../utils/dateUtils";
import useAuth from "../../hooks/useAuth";

export default function UserManagementPage({
  showActivityReport = true,
  showViewProfile = true,
}) {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [roles, setRoles] = useState([]);
  const [seatLimit, setSeatLimit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);
  const [tempPasswordsByUser, setTempPasswordsByUser] = useState({});
  const [resettingUserId, setResettingUserId] = useState(null);
  const [resetPasswordConfirmUser, setResetPasswordConfirmUser] =
    useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [detailUser, setDetailUser] = useState(null);
  const [showBulkReport, setShowBulkReport] = useState(false);
  const canUpdateUsers = usePermission(PERMISSIONS.USER_UPDATE);
  const canResetPassword = usePermission([
    PERMISSIONS.USER_CREATE,
    PERMISSIONS.USER_DELETE,
    PERMISSIONS.USER_UPDATE,
  ]);

  const fetchUsers = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await api.get("/users");
      setUsers(resp.data.users);
      setStats(resp.data.stats);
      setTempPasswordsByUser({});
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchRoles = React.useCallback(async () => {
    try {
      const resp = await api.get("/roles");
      setRoles(resp.data.filter((role) => !role.isSystem));
    } catch (err) {
      // ignore role panel failure for page rendering
    }
  }, [api]);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    subscriptionAPI
      .getStatus()
      .then((resp) => {
        if (resp.data?.totalUsersRegistered != null) {
          setSeatLimit(Number(resp.data.totalUsersRegistered));
        }
      })
      .catch(() => {});
  }, [fetchUsers, fetchRoles]);

  const { user: ownUser } = useAuth();

  const doResetPassword = async (user) => {
    try {
      setError(null);
      setResettingUserId(user.id);
      const resp = await api.post(`/users/${user.id}/reset-password`);
      setTempPasswordsByUser((prev) => ({
        ...prev,
        [user.id]: resp.data?.temporaryPassword || "",
      }));
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setResettingUserId(null);
    }
  };

  return (
    <div className="um-page">
      <header className="um-header">
        <div>
          <h1>User Management</h1>
          <p>Manage tenant users, role assignment, and account status.</p>
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
          {showActivityReport && (
            <>
              <button
                className="um-btn um-btn-secondary"
                style={{ alignSelf: "flex-start" }}
                onClick={() => setShowBulkReport(true)}
                disabled={loading || users.length === 0}
              >
                ↓ Download All Reports
              </button>
              <Link
                className="um-btn um-btn-secondary"
                to="/admin/users/attendance-report"
                style={{ textDecoration: "none", alignSelf: "flex-start" }}
              >
                Consolidated Activity Report
              </Link>
            </>
          )}
          <PermissionGate permission={PERMISSIONS.USER_CREATE}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: 4,
              }}
            >
              <button
                className="um-btn um-btn-primary"
                onClick={() => setShowAddModal(true)}
                disabled={seatLimit !== null && stats.total >= seatLimit}
                title={
                  seatLimit !== null && stats.total >= seatLimit
                    ? "Seat limit reached. Manage your subscription to add more users."
                    : undefined
                }
              >
                + Add User
              </button>
              {seatLimit !== null && stats.total >= seatLimit && (
                <span style={{ fontSize: 12, color: "#be123c" }}>
                  Seat limit reached.{" "}
                  <a href="/subscription" style={{ color: "#0f62fe" }}>
                    Manage subscription
                  </a>
                </span>
              )}
            </div>
          </PermissionGate>
        </div>
      </header>

      <section className="um-stats">
        <div className="um-stat-card">
          <span>Total Users</span>
          <strong>{stats.total || 0}</strong>
        </div>
        {seatLimit !== null && (
          <div
            className="um-stat-card"
            style={
              stats.total >= seatLimit
                ? { borderColor: "#fecaca", background: "#fff5f5" }
                : {}
            }
          >
            <span>Seats Used</span>
            <strong
              style={stats.total >= seatLimit ? { color: "#be123c" } : {}}
            >
              {stats.total || 0} / {seatLimit}
            </strong>
          </div>
        )}
        <div className="um-stat-card">
          <span>Active</span>
          <strong>{stats.active || 0}</strong>
        </div>
        <div className="um-stat-card">
          <span>Pending Setup</span>
          <strong>{stats.pendingPasswordChange || 0}</strong>
        </div>
        <div className="um-stat-card">
          <span>Inactive</span>
          <strong>{stats.inactive || 0}</strong>
        </div>
      </section>

      {error && <div className="um-error">{error}</div>}
      {loading ? (
        <div className="um-loading">Loading users...</div>
      ) : (
        <div className="um-table-wrap">
          <table className="um-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>WhatsApp</th>
                <th>Roles</th>
                <th>Status</th>
                <th>Password</th>
                <th>Stage Assignments</th>
                <th>Added</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    {user.firstName} {user.lastName}
                  </td>
                  <td>{user.email}</td>
                  <td>
                    {user.whatsappNumber || (
                      <em className="um-muted">Not set</em>
                    )}
                  </td>
                  <td>
                    {user.roles?.length ? (
                      <div className="um-chip-list">
                        {user.roles.map((role) => (
                          <span className="um-chip" key={role.id || role.name}>
                            {role.name || role}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <em className="um-muted">none</em>
                    )}
                  </td>
                  <td>
                    <div className="um-status-cell">
                      <span
                        className={`um-pill ${user.isActive ? "active" : "inactive"}`}
                      >
                        {user.isActive ? "Active" : "Inactive"}
                      </span>
                      {canUpdateUsers && user.id !== ownUser.id && (
                        <label className="um-switch">
                          <input
                            type="checkbox"
                            checked={!!user.isActive}
                            onChange={async () => {
                              try {
                                setError(null);
                                await api.patch(`/users/${user.id}`, {
                                  isActive: !user.isActive,
                                });
                                await fetchUsers();
                              } catch (err) {
                                setError(
                                  err?.response?.data?.message || err.message,
                                );
                              }
                            }}
                          />
                          <span className="um-slider" />
                        </label>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="um-password-cell">
                      {user.mustChangePassword ? (
                        <span className="um-pill pending">Pending</span>
                      ) : (
                        <span className="um-pill set">Set</span>
                      )}
                      {canResetPassword && user.id !== ownUser.id && (
                        <button
                          className="um-btn um-btn-icon"
                          type="button"
                          title="Reset password"
                          onClick={() => setResetPasswordConfirmUser(user)}
                          disabled={resettingUserId === user.id}
                        >
                          <span className="um-icon-lock" aria-hidden="true">
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M7 10V8a5 5 0 0 1 10 0v2"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                              <rect
                                x="5"
                                y="10"
                                width="14"
                                height="10"
                                rx="2"
                                stroke="currentColor"
                                strokeWidth="2"
                              />
                            </svg>
                          </span>
                          {resettingUserId === user.id ? "Resetting" : "Reset"}
                        </button>
                      )}
                      {tempPasswordsByUser[user.id] && (
                        <div className="um-temp-password">
                          <input
                            className="um-input"
                            type="text"
                            readOnly
                            value={tempPasswordsByUser[user.id]}
                          />
                          <button
                            className="um-btn um-btn-secondary"
                            type="button"
                            onClick={() =>
                              navigator.clipboard.writeText(
                                tempPasswordsByUser[user.id],
                              )
                            }
                          >
                            Copy
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="um-stage-stats">
                      <span className="um-chip um-chip-pending">
                        Pending: {user.pendingStageAssignments || 0}
                      </span>
                      <span className="um-chip um-chip-rejected">
                        Rejected: {user.rejectedStageAssignments || 0}
                      </span>
                      <span className="um-chip um-chip-done">
                        Done: {user.approvedStageAssignments || 0}
                      </span>

                      {/* <span className="um-chip">
                        Completed: {user.completedStageAssignments || 0}
                      </span> */}
                    </div>
                  </td>
                  <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                  <td>
                    <PermissionGate permission={PERMISSIONS.USER_UPDATE}>
                      <button
                        className="um-btn um-btn-secondary"
                        onClick={() => {
                          setEditingUser(user);
                          setShowEditModal(true);
                        }}
                      >
                        Edit User
                      </button>
                    </PermissionGate>
                    {showViewProfile && (
                      <button
                        className="um-btn um-btn-secondary"
                        style={{ marginTop: 4 }}
                        onClick={() => setDetailUser(user)}
                      >
                        View Profile
                      </button>
                    )}
                    {showActivityReport && (
                      <Link
                        className="um-btn um-btn-secondary"
                        style={{
                          marginTop: 4,
                          textDecoration: "none",
                          display: "inline-block",
                        }}
                        to={`/admin/users/${user.id}/activity-report`}
                        state={{ user }}
                      >
                        Activity Report
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAddModal && (
        <AddUserModal
          roles={roles}
          onClose={() => {
            setShowAddModal(false);
            setTempPassword(null);
          }}
          onAdd={async (data) => {
            try {
              setError(null);
              const resp = await api.post("/users", data);
              setTempPassword(resp.data.temporaryPassword);
              await fetchUsers();
              return null;
            } catch (err) {
              return (
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err.message
              );
            }
          }}
          tempPassword={tempPassword}
        />
      )}

      {showEditModal && editingUser && (
        <EditUserModal
          user={editingUser}
          roles={roles}
          onClose={() => {
            setShowEditModal(false);
            setEditingUser(null);
          }}
          onUpdate={async (payload) => {
            try {
              setError(null);
              await api.patch(`/users/${editingUser.id}`, payload);
              await fetchUsers();
              return true;
            } catch (err) {
              setError(err?.response?.data?.message || err.message);
              return false;
            }
          }}
        />
      )}

      {detailUser && (
        <UserDetailModal
          user={detailUser}
          onClose={() => setDetailUser(null)}
        />
      )}
      {showBulkReport && (
        <BulkActivityReportModal
          users={users}
          onClose={() => setShowBulkReport(false)}
        />
      )}
      {resetPasswordConfirmUser && (
        <ConfirmDialog
          title="Reset Password"
          message={`Reset password for ${resetPasswordConfirmUser.firstName} ${resetPasswordConfirmUser.lastName}? A temporary password will be generated.`}
          confirmLabel="Reset Password"
          confirmClass="btn-danger"
          onConfirm={() => {
            const u = resetPasswordConfirmUser;
            setResetPasswordConfirmUser(null);
            doResetPassword(u);
          }}
          onClose={() => setResetPasswordConfirmUser(null)}
        />
      )}
    </div>
  );
}

function BulkActivityReportModal({ users, onClose }) {
  const [selectedIds, setSelectedIds] = useState(
    () => new Set(users.map((u) => u.id)),
  );
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 29);
    return formatLocalDate(d);
  });
  const [to, setTo] = useState(todayString());
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  const q = search.trim().toLowerCase();
  const visible = users.filter(
    (u) =>
      !q ||
      u.firstName?.toLowerCase().includes(q) ||
      u.lastName?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q),
  );
  const allVisibleSelected =
    visible.length > 0 && visible.every((u) => selectedIds.has(u.id));

  const toggle = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };
  // Select/Deselect All acts on the filtered list, so a search narrows it.
  const toggleAllVisible = () => {
    const next = new Set(selectedIds);
    visible.forEach((u) =>
      allVisibleSelected ? next.delete(u.id) : next.add(u.id),
    );
    setSelectedIds(next);
  };

  const handleDownload = async () => {
    setDownloading(true);
    setError("");
    try {
      const resp = await usersAPI.downloadActivityReportsZip(
        Array.from(selectedIds),
        from,
        to,
      );
      const url = URL.createObjectURL(
        new Blob([resp.data], { type: "application/zip" }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `activity-reports-${from}-to-${to}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      onClose();
    } catch {
      setError("Failed to download reports");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="um-dialog-overlay" role="dialog" aria-modal="true">
      <div className="um-dialog um-modal-content" style={{ maxWidth: 560 }}>
        <div className="um-dialog-head">
          <h2>Download Activity Reports</h2>
          <button
            className="um-close-btn"
            type="button"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="um-dialog-body">
          <p className="um-note" style={{ marginTop: 0 }}>
            One PDF per selected user, bundled into a single ZIP.
          </p>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 12,
            }}
          >
            <input
              type="date"
              className="um-input"
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
            />
            <span>→</span>
            <input
              type="date"
              className="um-input"
              value={to}
              min={from}
              max={todayString()}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 8,
            }}
          >
            <input
              type="text"
              className="um-input"
              placeholder="Search users…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button
              type="button"
              className="um-btn um-btn-secondary"
              style={{ whiteSpace: "nowrap" }}
              onClick={toggleAllVisible}
            >
              {allVisibleSelected ? "Deselect All" : "Select All"}
            </button>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              maxHeight: 280,
              overflowY: "auto",
            }}
          >
            {visible.map((u) => (
              <label
                key={u.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  cursor: "pointer",
                  fontSize: 13,
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedIds.has(u.id)}
                  onChange={() => toggle(u.id)}
                />
                <span style={{ fontWeight: 500 }}>
                  {u.firstName} {u.lastName}
                </span>
                <span className="um-muted" style={{ fontSize: 11 }}>
                  {u.email}
                </span>
              </label>
            ))}
            {visible.length === 0 && (
              <div className="um-muted">No users match your search.</div>
            )}
          </div>
          {error && (
            <div className="um-error" style={{ marginTop: 12 }}>
              {error}
            </div>
          )}
        </div>
        <div className="um-dialog-footer">
          <button
            className="um-btn um-btn-primary"
            onClick={handleDownload}
            disabled={downloading || selectedIds.size === 0}
          >
            {downloading
              ? "Preparing ZIP…"
              : `Download ZIP (${selectedIds.size} user${selectedIds.size !== 1 ? "s" : ""})`}
          </button>
          <button
            className="um-btn um-btn-secondary"
            type="button"
            onClick={onClose}
            disabled={downloading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function AddUserModal({ roles, onClose, onAdd, tempPassword }) {
  const [data, setData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    whatsappNumber: "",
    telegramChatId: "",
    roleIds: [],
  });
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (tempPassword) return;
    setLoading(true);
    setLocalError("");
    const err = await onAdd(data);
    if (err) setLocalError(err);
    setLoading(false);
  };

  if (tempPassword) {
    return (
      <div className="um-dialog-overlay" role="dialog" aria-modal="true">
        <div className="um-dialog um-modal-content">
          <div className="um-dialog-head">
            <h2>User Created</h2>
            <button
              className="um-close-btn"
              type="button"
              onClick={onClose}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          <div className="um-dialog-body">
            <p>
              Share these credentials with {data.firstName || "the new user"}.
            </p>
            <div className="um-credential">
              <div>
                <label>Email</label>
                <strong>{data.email || "-"}</strong>
              </div>
              <div>
                <label>Temporary Password</label>
                <div className="um-credential-row">
                  <input
                    className="um-input"
                    type="text"
                    readOnly
                    value={tempPassword}
                  />
                  <button
                    className="um-btn um-btn-secondary"
                    type="button"
                    onClick={() => navigator.clipboard.writeText(tempPassword)}
                  >
                    Copy
                  </button>
                </div>
              </div>
            </div>
            <p className="um-note">
              This password will not be shown again. The user must change it on
              first login.
            </p>
          </div>
          <div className="um-dialog-footer">
            <button className="um-btn um-btn-primary" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="um-dialog-overlay" role="dialog" aria-modal="true">
      <div className="um-dialog um-modal-content">
        <div className="um-dialog-head">
          <h2>Add User</h2>
          <button
            className="um-close-btn"
            type="button"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit} className="um-form">
          <div className="um-dialog-body">
            <p className="um-subtext">
              Create a user and assign initial roles.
            </p>
            <div className="um-grid">
              <input
                className="um-input"
                placeholder="First Name"
                value={data.firstName}
                onChange={(e) =>
                  setData({ ...data, firstName: e.target.value })
                }
                required
              />
              <input
                className="um-input"
                placeholder="Last Name"
                value={data.lastName}
                onChange={(e) => setData({ ...data, lastName: e.target.value })}
                required
              />
            </div>
            <div style={{ marginBottom: 10 }}></div>
            <input
              className="um-input"
              type="email"
              placeholder="Email"
              value={data.email}
              onChange={(e) => setData({ ...data, email: e.target.value })}
              required
            />
            <div style={{ marginBottom: 10 }}></div>
            <input
              className="um-input"
              type="tel"
              placeholder="WhatsApp Number"
              value={data.whatsappNumber}
              onChange={(e) =>
                setData({ ...data, whatsappNumber: e.target.value })
              }
            />
            <p className="um-note">
              Optional. Needed to receive WhatsApp notifications.
            </p>
            <div style={{ marginBottom: 10 }}></div>
            <input
              className="um-input"
              type="text"
              placeholder="Telegram Chat ID"
              value={data.telegramChatId}
              onChange={(e) =>
                setData({ ...data, telegramChatId: e.target.value })
              }
            />
            <p className="um-note">
              Optional. Needed to receive Telegram notifications.
            </p>
            <div style={{ marginBottom: 10 }}></div>
            <select
              className="um-input"
              multiple
              value={data.roleIds}
              required
              onChange={(e) =>
                setData({
                  ...data,
                  roleIds: Array.from(e.target.selectedOptions).map(
                    (option) => option.value,
                  ),
                })
              }
            >
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
            <p className="um-note">Use Ctrl/Cmd to select multiple roles.</p>
            {localError && <div className="um-error">{localError}</div>}
          </div>
          <div className="um-dialog-footer">
            <button
              className="um-btn um-btn-primary"
              type="submit"
              disabled={loading}
            >
              {loading ? "Creating..." : "Create User"}
            </button>
            <button
              className="um-btn um-btn-secondary"
              type="button"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditUserModal({ user, roles, onClose, onUpdate }) {
  const [firstName, setFirstName] = useState(user.firstName || "");
  const [lastName, setLastName] = useState(user.lastName || "");
  const [whatsappNumber, setWhatsappNumber] = useState(
    user.whatsappNumber || "",
  );
  const [telegramChatId, setTelegramChatId] = useState(
    user.telegramChatId || "",
  );
  const [roleIds, setRoleIds] = useState(
    user.roleIds?.length
      ? user.roleIds
      : user.roles?.map((r) => r.id).filter(Boolean) || [],
  );
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState("");
  const currentRoleLabel =
    user.roles?.length > 0
      ? user.roles.map((role) => role.name || role).join(", ")
      : "No roles assigned";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError("");
    if (!firstName.trim() || !lastName.trim()) {
      setLocalError("First name and last name are required");
      return;
    }
    if (!roleIds.length) {
      setLocalError("Please select at least one role");
      return;
    }
    setLoading(true);
    const ok = await onUpdate({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      whatsappNumber: whatsappNumber.trim() || null,
      telegramChatId: telegramChatId.trim() || null,
      roleIds,
    });
    setLoading(false);
    if (ok) {
      onClose();
    }
  };

  return (
    <div className="um-dialog-overlay" role="dialog" aria-modal="true">
      <div className="um-dialog um-modal-content">
        <div className="um-dialog-head">
          <h2>
            Edit Roles for {user.firstName} {user.lastName}
          </h2>
          <button
            className="um-close-btn"
            type="button"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit} className="um-form">
          <div className="um-dialog-body">
            <div className="um-grid">
              <input
                className="um-input"
                placeholder="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
              <input
                className="um-input"
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </div>
            <div style={{ marginBottom: 10 }}></div>
            <input
              className="um-input"
              type="tel"
              placeholder="WhatsApp Number"
              value={whatsappNumber}
              onChange={(e) => setWhatsappNumber(e.target.value)}
            />
            <p className="um-note">
              Optional. Needed to receive WhatsApp notifications.
            </p>
            <div style={{ marginBottom: 10 }}></div>
            <input
              className="um-input"
              type="text"
              placeholder="Telegram Chat ID"
              value={telegramChatId}
              onChange={(e) => setTelegramChatId(e.target.value)}
            />
            <p className="um-note">
              Optional. Needed to receive Telegram notifications.
            </p>
            <div style={{ marginBottom: 10 }}></div>
            <p className="um-note">
              Current roles: <strong>{currentRoleLabel}</strong>
            </p>
            {roles.length === 0 && (
              <p className="um-note">No editable roles are available.</p>
            )}
            {localError && <div className="um-error">{localError}</div>}
            <select
              className="um-input"
              multiple
              value={roleIds}
              required
              disabled={roles.length === 0 || loading}
              onChange={(e) =>
                setRoleIds(
                  Array.from(e.target.selectedOptions).map(
                    (option) => option.value,
                  ),
                )
              }
            >
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
            <p className="um-note">Use Ctrl/Cmd to select multiple roles.</p>
          </div>
          <div className="um-dialog-footer">
            <button
              className="um-btn um-btn-primary"
              type="submit"
              disabled={loading || roles.length === 0}
            >
              {loading ? "Saving..." : "Save Roles"}
            </button>
            <button
              className="um-btn um-btn-secondary"
              type="button"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UserDetailModal({ user, onClose }) {
  const canViewSchedule = usePermission([
    PERMISSIONS.SCHEDULE_VIEW_ALL,
    PERMISSIONS.SCHEDULE_VIEW_OWN,
  ]);
  const canViewLogs = usePermission([
    PERMISSIONS.TIME_LOG_VIEW_ALL,
    PERMISSIONS.TIME_LOG_VIEW_OWN,
  ]);
  const tabs = [
    { id: "basic", label: "Basic Info", visible: true },
    { id: "roles", label: "Roles", visible: true },
    { id: "logs", label: "Time Logs", visible: canViewLogs },
  ].filter((tab) => tab.visible);
  const [activeTab, setActiveTab] = useState(tabs[0]?.id || "basic");

  return (
    <div className="um-dialog-overlay" role="dialog" aria-modal="true">
      <div className="um-dialog um-modal-content">
        <div className="um-dialog-head">
          <h2>
            {user.firstName} {user.lastName}
          </h2>
          <button
            className="um-close-btn"
            type="button"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="um-dialog-body">
          <div className="project-actions" style={{ marginBottom: 12 }}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={
                  activeTab === tab.id ? "btn-primary" : "btn-secondary"
                }
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {canViewSchedule && (
            <div className="schedule-redirect">
              <h4>Work Schedule & Task Queue</h4>
              <p>
                Manage {user.firstName}'s working days, weekly offs, and task
                priority in the Planner.
              </p>
              <Link to={`/planner?userId=${user.id}`} className="btn-secondary">
                Open in Planner
              </Link>
            </div>
          )}
          {activeTab === "basic" && (
            <div className="panel">
              <div className="small-muted">Email</div>
              <div style={{ fontWeight: 600 }}>{user.email}</div>
              <div className="small-muted" style={{ marginTop: 8 }}>
                Status: {user.isActive ? "Active" : "Inactive"}
              </div>
            </div>
          )}
          {activeTab === "roles" && (
            <div className="panel">
              <div className="small-muted">Roles</div>
              {user.roles?.length ? (
                <div className="chip-list">
                  {user.roles.map((role) => (
                    <span className="detail-chip" key={role.id || role.name}>
                      {role.name || role}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="small-muted">No roles assigned.</div>
              )}
            </div>
          )}
          {activeTab === "logs" && <UserTimeLogsTab user={user} />}
        </div>
      </div>
    </div>
  );
}
